import { PDFDocument, rgb, PDFName, PDFArray, PDFNumber } from 'pdf-lib';
import type { LayoutPlan } from '../models/layout';
import type { LegalBlock, LegalRun } from '../models/ast';
import { FontManager } from '../pdf/pdf-fonts';
import { attachEdxMetadata, attachDocMetadata, attachAiMetadata, setPdfAInfo } from '../pdf/pdf-metadata';
import { buildMinimalXmp } from '../pdf/pdf-xmp';
import { getStylesRegistry } from '../styles/registry';
import { resolveRunStyle } from '../styles/resolveStyle';

export interface PdfOptions {
  pageWidthPx: number;
  pageHeightPx: number;
  includeLinks?: boolean;
  includeAI?: boolean;
  debugLinks?: boolean;
  imageResolver?: (src: string) => Promise<{ data: Uint8Array; width?: number; height?: number } | null>;
}

// Minimal Phase 1-2: render real line text using run grapheme ranges; add basic internal links for cross-refs
export async function pdfFromLayoutPlan(plan: LayoutPlan, ast: LegalBlock[], opts: PdfOptions & { pdfa?: boolean; docMetaJson?: string; aiMetaJson?: string; fontConfigs?: import('./pdf-fonts').FontConfig[] }): Promise<Uint8Array> {
  // If we later introduce Chromium exporter, we can branch here. For now we keep pdf-lib path.
  const doc = await PDFDocument.create();
  const fontManager = new FontManager(opts.fontConfigs);
  const namePairs: any[] = []; // [name, destArray, ...]
  const addedNames = new Set<string>();
  const styles = getStylesRegistry();
  const blockById: Record<string, any> = {};
  const imageDrawn: Set<string> = new Set();

  function sanitizeName(input: string): string {
    return `edx_${input.replace(/[^A-Za-z0-9_.-]/g, '_').slice(0, 64)}`;
  }

  // Build runId -> run map for text lookup and block map
  const runById: Record<string, LegalRun> = {};
  for (const b of ast || []) {
    const anyB: any = b as any;
    blockById[anyB.id] = anyB;
    const children = anyB?.children as any[] | undefined;
    if (Array.isArray(children)) {
      for (const ch of children) {
        if (ch && typeof ch === 'object' && 'id' in ch && 'text' in ch) {
          runById[(ch as LegalRun).id] = ch as LegalRun;
        }
        // Nested lists may contain blocks
        if (ch && typeof ch === 'object' && 'type' in ch && (ch as any).children) {
          const nestedChildren = (ch as any).children as any[];
          for (const nch of nestedChildren) {
            if (nch && typeof nch === 'object' && 'id' in nch && 'text' in nch) {
              runById[(nch as LegalRun).id] = nch as LegalRun;
            }
          }
        }
      }
    }
  }

  // Helper: grapheme-aware slice
  function sliceGraphemes(text: string, start: number, endInclusive: number): string {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seg = new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' });
      const parts = Array.from(seg.segment(text)).map((s: any) => s.segment as string);
      return parts.slice(start, endInclusive + 1).join('');
    } catch {
      return text.slice(start, endInclusive + 1);
    }
  }

  // Pre-collect glyph usage per font key to improve subsetting
  const glyphsByFontKey: Map<string, Set<string>> = new Map();
  function addGlyphsFor(fontKey: string, text: string) {
    let set = glyphsByFontKey.get(fontKey);
    if (!set) { set = new Set<string>(); glyphsByFontKey.set(fontKey, set); }
    for (const ch of text) set.add(ch);
  }

  for (let pageIndex = 0; pageIndex < plan.pages.length; pageIndex++) {
    const p = plan.pages[pageIndex];
    const page = doc.addPage([opts.pageWidthPx, opts.pageHeightPx]);
    const pageAnnots: any[] = [];

    // Optional debug regions (commented by default)
    // page.drawRectangle({ x: p.header.x, y: opts.pageHeightPx - (p.header.y + p.header.height), width: p.header.width, height: p.header.height, color: rgb(0,0.7,0.5), opacity: 0.03 });
    // page.drawRectangle({ x: p.footer.x, y: opts.pageHeightPx - (p.footer.y + p.footer.height), width: p.footer.width, height: p.footer.height, color: rgb(1,0.6,0.1), opacity: 0.03 });

    // 1) Draw text lines (and images at first occurrence per block)
    for (const lb of p.body.lineBoxes) {
      const x = lb.x;
      const baseline = lb.baseline; // from top
      const pdfY = opts.pageHeightPx - baseline;
      const run = runById[lb.runId];
      const block = blockById[lb.blockId];

      // Draw image once per Image block if resolver provided
      if (block?.type === 'Image' && !imageDrawn.has(lb.blockId) && opts.imageResolver) {
        try {
          const src: string = block?.attrs?.src || '';
          if (src) {
            const resolved = await opts.imageResolver(src);
            if (resolved?.data) {
              // Try embedding as PNG first, then JPEG
              let embedded: any = null;
              try { embedded = await (doc as any).embedPng ? await (doc as any).embedPng(resolved.data) : null; } catch {}
              if (!embedded) { try { embedded = await (doc as any).embedJpg ? await (doc as any).embedJpg(resolved.data) : null; } catch {} }
              if (embedded) {
                const w = (block.attrs?.width as number) || resolved.width || embedded.width || 200;
                const h = (block.attrs?.height as number) || resolved.height || embedded.height || 150;
                const imgY = opts.pageHeightPx - (lb.y + h); // align top-left
                embedded.scale ? page.drawImage(embedded, { x: lb.x, y: imgY, width: w, height: h }) : page.drawImage(embedded, { x: lb.x, y: imgY, width: w, height: h });
                imageDrawn.add(lb.blockId);
              }
            }
          }
        } catch {}
      }
      if (run && typeof run.text === 'string') {
        const segment = sliceGraphemes(run.text, lb.startGrapheme, lb.endGrapheme);
        // Resolve per-run style using paragraph + char styles
        // block already defined above
        const paragraphStyle = block?.styleId ? styles.paragraphStyles[block.styleId] : undefined;
        const rs: any = resolveRunStyle(paragraphStyle as any, run);
        const family: string | undefined = rs?.fontFamily;
        const weight: number | undefined = rs?.fontWeight;
        const italic: boolean | undefined = rs?.fontStyle === 'italic' || rs?.italic === true;
        const sizePt: number = typeof rs?.fontSizePt === 'number' ? rs.fontSizePt : 12;
        // Collect glyphs for this font key
        const fontKey = `${family || 'Helvetica'}-${italic ? 'italic' : 'normal'}-${weight || 400}`;
        addGlyphsFor(fontKey, segment);
        const font = await fontManager.getFont(doc, family, weight, italic);
        page.drawText(segment, { x, y: pdfY, size: sizePt, font, color: rgb(0, 0, 0) });
      } else {
        // Fallback: draw a faint box if run text missing
        page.drawRectangle({ x, y: opts.pageHeightPx - (lb.y + lb.height), width: lb.width, height: lb.height, borderColor: rgb(0.2,0.5,0.9), borderWidth: 0.3, opacity: 0.1 });
      }
      // Register a named destination for the first appearance of this block
      const destName = sanitizeName(`b_${lb.blockId}`);
      if (!addedNames.has(destName)) {
        const dest = doc.context.obj([
          (page as any).ref,
          PDFName.of('XYZ'),
          PDFNumber.of(lb.x),
          PDFNumber.of(opts.pageHeightPx - lb.baseline),
          doc.context.obj(null),
        ]) as PDFArray;
        namePairs.push(doc.context.obj(destName));
        namePairs.push(dest);
        addedNames.add(destName);
      }
    }

    // 2) Add cross-ref link annotations (basic GoTo) if requested
    if (opts.includeLinks && Array.isArray((plan as any).crossRefs)) {
      const crossRefs = (plan as any).crossRefs as Array<{ sourceRunId: string; targetBlockId: string; targetPage?: number; status: string }>;
      // Build target anchors: first lineBox per blockId in each page
      const targetAnchor: Record<string, { pageIndex: number; x: number; y: number }> = {};
      for (let pi = 0; pi < plan.pages.length; pi++) {
        for (const lb of plan.pages[pi].body.lineBoxes) {
          if (!targetAnchor[lb.blockId]) {
            targetAnchor[lb.blockId] = { pageIndex: pi, x: lb.x, y: lb.baseline };
          }
        }
      }
      for (const cr of crossRefs) {
        if (cr.status !== 'resolved') continue;
        // Source rects: all lineBoxes on this page for the source runId
        const sourceBoxes = p.body.lineBoxes.filter((lb) => lb.runId === cr.sourceRunId);
        const dest = targetAnchor[cr.targetBlockId];
        if (!dest) continue;
        const destName = sanitizeName(`b_${cr.targetBlockId}`);
        for (const sb of sourceBoxes) {
          const x1 = sb.x;
          // Place clickable rect around baseline for better alignment with text
          const underlineHeight = 6;
          const y1 = opts.pageHeightPx - sb.baseline - Math.floor(underlineHeight / 2);
          const x2 = sb.x + sb.width;
          const y2 = y1 + underlineHeight;
          if (opts.debugLinks) {
            // draw a bold highlight + outline for the clickable area
            page.drawRectangle({ x: x1, y: y1 + 1, width: x2 - x1, height: 3, color: rgb(0.2, 0.5, 0.9), opacity: 0.4 });
            page.drawRectangle({ x: x1, y: y1, width: x2 - x1, height: sb.height, borderColor: rgb(0.2, 0.5, 0.9), borderWidth: 1, opacity: 0.35 });
          }
          const annot = doc.context.obj({
            Type: PDFName.of('Annot'),
            Subtype: PDFName.of('Link'),
            Rect: doc.context.obj([x1, y1, x2, y2]),
            Border: doc.context.obj([0, 0, 0]),
            Dest: doc.context.obj(destName)
          });
          pageAnnots.push(annot);
        }
      }
    }

    // 3) Footnote anchor ↔ footer links (same-page GoTo)
    if (opts.includeLinks) {
      // Gather unique footnote anchors on this page in visual order
      const seen = new Set<string>();
      const anchors: Array<{ anchorId: string; x1: number; y1: number; x2: number; y2: number }> = [];
      for (const lb of p.body.lineBoxes) {
        const run = runById[lb.runId];
        const aid = run?.annotations?.footnoteAnchorId;
        if (!aid || seen.has(aid)) continue;
        seen.add(aid);
        const x1 = lb.x;
        const underlineHeight = 6;
        const y1 = opts.pageHeightPx - lb.baseline - Math.floor(underlineHeight / 2);
        const x2 = lb.x + lb.width;
        const y2 = y1 + underlineHeight;
        anchors.push({ anchorId: aid, x1, y1, x2, y2 });
      }
      // Map in order to footer boxes
      const footers = p.footer.footnotes || [];
      const count = Math.min(anchors.length, footers.length);
      for (let i = 0; i < count; i++) {
        const a = anchors[i];
        const fb = footers[i];
        const footName = sanitizeName(`fn_p${pageIndex}_${i}`);
        const destY = opts.pageHeightPx - (fb.area.y + 2); // near top of footnote area
        const destArray = doc.context.obj([
          (page as any).ref,
          PDFName.of('XYZ'),
          PDFNumber.of(fb.area.x),
          PDFNumber.of(destY),
          doc.context.obj(null)
        ]) as PDFArray;
        namePairs.push(doc.context.obj(footName));
        namePairs.push(destArray);
        // Link from anchor in body to footer
        if (opts.debugLinks) {
          // underline + outline at anchor
          page.drawRectangle({ x: a.x1, y: a.y1 + 1, width: a.x2 - a.x1, height: 3, color: rgb(0.9, 0.5, 0.2), opacity: 0.4 });
          page.drawRectangle({ x: a.x1, y: a.y1, width: a.x2 - a.x1, height: a.y2 - a.y1, borderColor: rgb(0.9, 0.5, 0.2), borderWidth: 1, opacity: 0.35 });
        }
        const toFooter = doc.context.obj({
          Type: PDFName.of('Annot'),
          Subtype: PDFName.of('Link'),
          Rect: doc.context.obj([a.x1, a.y1, a.x2, a.y2]),
          Border: doc.context.obj([0, 0, 0]),
          Dest: doc.context.obj(footName)
        });
        pageAnnots.push(toFooter);
        // Link back from footer box to anchor
        const anchorName = sanitizeName(`an_${a.anchorId}`);
        const backDestArray = doc.context.obj([
          (page as any).ref,
          PDFName.of('XYZ'),
          PDFNumber.of(a.x1),
          PDFNumber.of(a.y2),
          doc.context.obj(null)
        ]) as PDFArray;
        namePairs.push(doc.context.obj(anchorName));
        namePairs.push(backDestArray);
        const fx1 = fb.area.x;
        const fy1 = opts.pageHeightPx - (fb.area.y + fb.area.height);
        const fx2 = fb.area.x + fb.area.width;
        const fy2 = fy1 + fb.area.height;
        if (opts.debugLinks) {
          // footer box outline (thicker)
          page.drawRectangle({ x: fx1, y: fy1, width: fx2 - fx1, height: fy2 - fy1, borderColor: rgb(0.9, 0.5, 0.2), borderWidth: 1.2, opacity: 0.35 });
        }
        const backLink = doc.context.obj({
          Type: PDFName.of('Annot'),
          Subtype: PDFName.of('Link'),
          Rect: doc.context.obj([fx1, fy1, fx2, fy2]),
          Border: doc.context.obj([0, 0, 0]),
          Dest: doc.context.obj(anchorName)
        });
        pageAnnots.push(backLink);
      }
    }

    // 4) Commit annotations for this page (if any)
    if (pageAnnots.length > 0) {
      const node = (page as any).node;
      node.set(PDFName.of('Annots'), doc.context.obj(pageAnnots));
    }
  }

  // Attach metadata (best-effort)
  await attachEdxMetadata(doc, {
    planHash: (plan as any)?.planHash,
    pageCount: plan.pages?.length || 0,
    crossRefCount: Array.isArray((plan as any).crossRefs) ? (plan as any).crossRefs.length : 0,
  });
  if (opts.docMetaJson) await attachDocMetadata(doc, opts.docMetaJson);
  if (opts.aiMetaJson) await attachAiMetadata(doc, opts.aiMetaJson);
  if (opts.pdfa) {
    setPdfAInfo(doc, { title: 'EdgeDocs PDF', creator: 'EdgeDocs', producer: 'EdgeDocs PDF Engine', createdAt: new Date() });
    try {
      const xmp = buildMinimalXmp({ title: 'EdgeDocs PDF', creator: 'EdgeDocs', createdAt: new Date(), pdfa: true });
      // Store XMP as Metadata stream if available
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const catalog: any = (doc as any).catalog;
      const stream = doc.context.flateStream(xmp, { Type: PDFName.of('Metadata'), Subtype: PDFName.of('XML') });
      catalog.set(PDFName.of('Metadata'), stream);
    } catch {}
  }

  // Note: Quick PDF intentionally omits tagging to keep the client path lightweight.

  // Names tree registration for destinations
  if (namePairs.length > 0) {
    const namesArray = doc.context.obj(namePairs);
    const destsDict = doc.context.obj({ Names: namesArray });
    const namesDict = doc.context.obj({ Dests: destsDict });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (doc.catalog as any).set(PDFName.of('Names'), namesDict);
  }

  return await doc.save();
}


